<script lang="ts">
	import Modal from '$lib/components/Modal/Modal.svelte';
	import { getHubStores } from '$lib/stores/hubStores';
	import { _ } from 'svelte-i18n';
	import RoleSelector, {
		type Role,
		type RoleOption,
	} from '$lib/components/RoleSelector/RoleSelector.svelte';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import Button from '$lib/components/Button/Button.svelte';
	import Tooltip from '$lib/components/Tooltip/Tooltip.svelte';
	import { toastMessages } from '$lib/stores/toastMessageStore';

	const { hub, restApi } = getHubStores();

	let emailField = '';
	let inviteMessage = '';
	let selectedRole: Role;
	export let isOpen = false;
	export let onSuccess = () => {
		// Nothing
	};

	const emailParser = (emails: string): string[] => {
		return emails.split(/[,;\s]/).filter((email) => email.length > 0);
	};

	const onSubmit = async (event: Event) => {
		event.preventDefault();
		const inviteeEmails = emailParser(emailField);

		const data = {
			invitee_role: selectedRole as string,
			message: inviteMessage,
		};

		const promises = inviteeEmails.map((email) => {
			return restApi.inviteViaEmail($hub.id, { ...data, invitee_email: email });
		});

		await Promise.all(promises).then((responses) =>
			responses.forEach((resp, index) => {
				if (resp.error) {
					if (resp.status == 409) {
						toastMessages.danger(
							$_('account.invite.already.exists', { values: { email: inviteeEmails[index] } })
						);
					} else {
						toastMessages.danger(
							$_('account.invite.failed', { values: { email: inviteeEmails[index] } })
						);
					}
				} else {
					toastMessages.success(
						$_('account.invite.sent.success', { values: { userName: inviteeEmails[index] } })
					);
					onSuccess();
				}
			})
		);
		emailField = '';
		isOpen = false;
	};

	const getRenderedOptions = () => {
		return (['collaborator', 'owner', 'moderator', 'member'] satisfies RoleOption[]).filter(
			(role) => {
				// We allow inviting members only if the hub is a stakeholder
				if (role === 'member') {
					return $hub.type === 'sparq3stakeholder';
				}

				return $hub.user_context.perms.team.invites[role];
			}
		);
	};
</script>

<Modal bind:isOpen>
	<div slot="target">
		<slot />
	</div>
	<div slot="title" class="title">
		<h2>{$_('hubs.invite.to.hub', { values: { hubName: $hub.name } })}</h2>
	</div>
	<div class="modal" slot="content">
		<div class="modalContent">
			<div class="roleSelector">
				<RoleSelector bind:selectedRole renderedOptions={getRenderedOptions()} />
			</div>
			<form>
				<label>
					<h3>{$_('common.emails')}</h3>
					<textarea
						rows="8"
						placeholder={$_('account.enter.list.of.emails')}
						bind:value={emailField}
					/>
				</label>
				<label>
					<h3>{$_('common.message')}</h3>
					<textarea
						rows="4"
						maxLength="1024"
						placeholder={$_('account.invite.email.message')}
						bind:value={inviteMessage}
					/>
				</label>
				<div class="lastRow">
					<Tooltip position="left" unmountDelay={1000}>
						<Icon icon="warning" size="20" color="orange" />
						<div slot="content">
							{$_('hubs.email.compliance.warning')}
							<a
								href="http://crtc.gc.ca/eng/internet/anti.htm"
								target="blank"
								rel="noopener noreferrer"
							>
								{$_('hubs.learn.more')}
							</a>
						</div>
					</Tooltip>
					<Button disabled={!emailField.length} on:click={onSubmit}>{$_('hubs.send.invite')}</Button
					>
				</div>
			</form>
		</div>
	</div>
</Modal>

<style>
	.title {
		display: flex;
		position: relative;
		inline-size: 100%;
	}

	.modalContent {
		inline-size: 80vw;
		max-inline-size: 600px;
		margin: var(--gutterMedium);
	}

	h2 {
		margin: 0;
		font-size: var(--fontSizeLarger);
		font-weight: 400;
	}

	h3 {
		margin: 0;
		color: gray;
		font-size: var(--fontSizeLarger);
		font-weight: 400;
	}

	.roleSelector {
		display: flex;
		position: relative;
		flex-direction: row-reverse;
		inset-block-start: 20px;
	}

	form,
	label {
		display: flex;
		flex-direction: column;
		gap: var(--gutterSmall);
	}

	label {
		color: var(--LIGHT_GRAY);
		font-size: var(--fontSizeNormal);
	}

	textarea {
		overflow-y: auto;
		font-size: var(--fontSizeSmall);
		resize: none;
	}

	.lastRow {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
</style>
